import {
  NOTIFICATION_TYPE_LABELS,
  NOTIFICATION_TYPE_VARIABLES,
  NOTIFICATION_TYPES,
  type NotificationType,
} from '@lms/shared'
import { Edit, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateTemplate,
  useDeleteTemplate,
  useNotificationTemplates,
  useUpdateTemplate,
} from '@/hooks/use-notification-templates'

interface TemplateForm {
  notificationType: string
  subject: string
  body: string
  availableVariables: string
}

const EMPTY_FORM: TemplateForm = {
  notificationType: '',
  subject: '',
  body: '',
  availableVariables: '',
}

export default function NotificationTemplatesPage() {
  const { data, isLoading } = useNotificationTemplates()
  const createTemplate = useCreateTemplate()
  const updateTemplate = useUpdateTemplate()
  const deleteTemplate = useDeleteTemplate()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM)
  const usableVariables =
    NOTIFICATION_TYPE_VARIABLES[form.notificationType as NotificationType] ?? []

  const templates = Array.isArray(data) ? data : []

  const openCreate = () => {
    setEditId(null)
    setForm(EMPTY_FORM)
    setDialogOpen(true)
  }

  const openEdit = (t: any) => {
    setEditId(t.id)
    setForm({
      notificationType: t.notificationType,
      subject: t.subject,
      body: t.body,
      availableVariables: (t.availableVariables ?? []).join(', '),
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    const variables = form.availableVariables
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)

    try {
      if (editId) {
        await updateTemplate.mutateAsync({
          id: editId,
          data: { subject: form.subject, body: form.body, availableVariables: variables },
        })
        toast.success('テンプレートを更新しました')
      } else {
        await createTemplate.mutateAsync({
          notificationType: form.notificationType as any,
          subject: form.subject,
          body: form.body,
          availableVariables: variables,
        })
        toast.success('テンプレートを作成しました')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存に失敗しました')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('このテンプレートを削除しますか？')) return
    try {
      await deleteTemplate.mutateAsync(id)
      toast.success('テンプレートを削除しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '削除に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">通知テンプレート管理</h1>
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">通知テンプレート管理</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          新規作成
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">テンプレートが登録されていません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {templates.map((t: any) => (
            <Card key={t.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Badge variant="outline" className="mb-2">
                      {NOTIFICATION_TYPE_LABELS[t.notificationType as NotificationType] ??
                        t.notificationType}
                    </Badge>
                    <CardTitle className="text-base">{t.subject}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDelete(t.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3">{t.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? 'テンプレート編集' : 'テンプレート作成'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editId && (
              <div className="space-y-2">
                <Label>通知タイプ</Label>
                <Select
                  value={form.notificationType}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      notificationType: v,
                      availableVariables: (
                        NOTIFICATION_TYPE_VARIABLES[v as NotificationType] ?? []
                      ).join(', '),
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTIFICATION_TYPES.map((nt) => (
                      <SelectItem key={nt} value={nt}>
                        {NOTIFICATION_TYPE_LABELS[nt]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>件名</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="メール件名"
              />
            </div>
            <div className="space-y-2">
              <Label>本文</Label>
              {usableVariables.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  使用できる変数: {usableVariables.map((v) => `{{${v}}}`).join(' / ')}
                  （改行は &lt;br&gt; が使えます）
                </p>
              )}
              <Textarea
                rows={6}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="テンプレート本文"
              />
            </div>
            <div className="space-y-2">
              <Label>使用可能変数（カンマ区切り）</Label>
              <Input
                value={form.availableVariables}
                onChange={(e) => setForm({ ...form, availableVariables: e.target.value })}
                placeholder="userName, trainingTitle, enrollmentDate"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleSave}
              disabled={createTemplate.isPending || updateTemplate.isPending}
            >
              {createTemplate.isPending || updateTemplate.isPending ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
